// Number and date formatters per INTERFACE-SPECIFICATION.md §12.6.
//
// Rules enforced:
//   1. Units in headers, not cells.
//   2. Fixed decimal places per column.
//   3. Right-aligned (handled by .num CSS class in base.css).
//   4. Tabular numerals (font-feature-settings: 'tnum').
//   5. Thousands separators on amounts ≥ 1,000.
//   6. Ranges as paired columns.
//   7. Delta sign: + or − (U+2212), never colour alone. Unchanged = "=".
//   8. Sentinels and absences as em dash, italic.
//   9. Column-header weight matches body.
//  10. Column width follows the longest expected value.

const MINUS = '−';        // U+2212 MINUS SIGN
const EM_DASH = '—';      // U+2014 EM DASH

const rateFormatter = new Intl.NumberFormat('en-AU', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  useGrouping: false
});

const amountFormatter = new Intl.NumberFormat('en-AU', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
  useGrouping: true
});

const amountThousandsFormatter = new Intl.NumberFormat('en-AU', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
  useGrouping: true
});

export function formatRate(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return EM_DASH;
  return rateFormatter.format(value);
}

export function formatAmount(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return EM_DASH;
  return amountFormatter.format(value);
}

export function formatAmountThousands(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return EM_DASH;
  if (value >= 1000) {
    return `${amountThousandsFormatter.format(value / 1000)}K`;
  }
  return amountFormatter.format(value);
}

export function formatRateDelta(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return EM_DASH;
  if (value === 0) return '=';
  const magnitude = rateFormatter.format(Math.abs(value));
  return value > 0 ? `+${magnitude}` : `${MINUS}${magnitude}`;
}

export function formatAmountDelta(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return EM_DASH;
  if (value === 0) return '=';
  const magnitude = amountFormatter.format(Math.abs(value));
  return value > 0 ? `+${magnitude}` : `${MINUS}${magnitude}`;
}

export function deltaCssClass(value: number | null | undefined): 'delta-up' | 'delta-down' | 'delta-zero' | 'faint' {
  if (value === null || value === undefined || Number.isNaN(value)) return 'faint';
  if (value > 0) return 'delta-up';
  if (value < 0) return 'delta-down';
  return 'delta-zero';
}

export function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return EM_DASH;
  return `${rateFormatter.format(value)}%`;
}

export function formatAmountCurrency(value: number | null | undefined, currency: string = 'AUD'): string {
  if (value === null || value === undefined || Number.isNaN(value)) return EM_DASH;
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
}

// INTERFACE-SPECIFICATION.md §9.6: "1 June 2026, 00:00 AEST" — full
// month name, not abbreviated.
const monthFormatter = new Intl.DateTimeFormat('en-AU', {
  day: 'numeric',
  month: 'long',
  year: 'numeric'
});

export function formatLocalDate(localIso: string | null | undefined): string {
  if (!localIso) return EM_DASH;
  // localIso is a wall-clock ISO without a Z suffix; format the date
  // component only (the time is implicit at midnight for catalogue
  // versions). Newer ICU returns narrow no-break spaces between the
  // date parts; replace them with regular spaces so the layout matches
  // the wireframes literally.
  const datePart = localIso.split('T')[0] ?? localIso;
  const [y, m, d] = datePart.split('-').map((s) => Number(s));
  if (!y || !m || !d) return localIso;
  const date = new Date(Date.UTC(y, m - 1, d));
  return monthFormatter.format(date).replace(/ /g, ' ').replace(/ /g, ' ');
}

// Format a 4-part effective_from timestamp as "16 May 2026 AEST" or
// EM_DASH when null. The clamp-note partner formatter
// (formatClampedFromNote) renders the proposer's pre-clamp intent in
// the same shape so the UI can show both consistently:
//   "16 May 2026 AEST  (clamped from proposer's 15 May 2026 AEST intent)"
export function formatEffectiveTimestamp(
  ts: { local?: string | null; tz_abbreviation_at_effective?: string | null } | null | undefined
): string {
  if (!ts?.local) return EM_DASH;
  const date = formatLocalDate(ts.local);
  const tz = ts.tz_abbreviation_at_effective ? ` ${ts.tz_abbreviation_at_effective}` : '';
  return `${date}${tz}`;
}

// Render the "clamped from proposer's X intent" sub-line when a
// manifest entry carries proposed_effective_from (the clamp ran at
// upsert because the proposer's stated effective_from was before the
// merge instant — Greg-signed-off 2026-05-16 Option B). Returns null
// when no clamp applied so callers can render unconditionally.
export function formatClampedFromNote(
  proposed: { local?: string | null; tz_abbreviation_at_effective?: string | null } | null | undefined
): string | null {
  if (!proposed?.local) return null;
  return `clamped from proposer's ${formatEffectiveTimestamp(proposed)} intent`;
}

// Format a UTC ISO timestamp as "11 May 2026, 14:22" in the viewer's
// local timezone. Used for review-comment timestamps on the Pending
// Review card and the Revise interface (INTERFACE-SPECIFICATION.md
// sections 8.1 and 8.4).
export function formatCommentTimestamp(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const dateStr = d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  const timeStr = d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${dateStr}, ${timeStr}`;
}

export function formatEffectiveFrom(localIso: string | null | undefined, tzAbbreviation?: string | null): string {
  if (!localIso) return EM_DASH;
  const datePart = formatLocalDate(localIso);
  const timePart = localIso.split('T')[1]?.slice(0, 5) ?? '00:00';
  const tz = tzAbbreviation ? ` ${tzAbbreviation}` : '';
  return `${datePart}, ${timePart}${tz}`;
}

export function relativeTimeAgo(iso: string | null | undefined, nowMs?: number): string {
  if (!iso) return EM_DASH;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return EM_DASH;
  const now = nowMs ?? Date.now();
  const diffMs = now - then;
  const seconds = Math.round(diffMs / 1000);
  if (seconds < 5) return 'just now';
  if (seconds < 60) return `${seconds} seconds ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`;
  const years = Math.round(months / 12);
  return `${years} year${years === 1 ? '' : 's'} ago`;
}

// Exports for tests.
export const __conventions = { MINUS, EM_DASH };
